import { useEffect, useState } from "react";
import { Icon } from "../lib/Icon";
import { Avatar, EmptyState } from "../lib/components";
import { useAuth } from "../contexts/AuthContext";
import { useInventory } from "../contexts/InventoryContext";
import { useToast } from "../lib/toast";
import {
  acceptProposal,
  cancelProposal,
  completeProposal,
  fetchMyProposals,
  rejectProposal,
  type TradeProposal,
  type TradeStatus,
} from "../api/trades";
import { ApiError } from "../api/client";
import type { Route } from "../App";
import { ProposeTradeModal } from "./ProposeTradeModal";
import { ReviewModal } from "./ReviewModal";
import { fetchMyReviewForTrade } from "../api/reviews";

type Tab = "incoming" | "outgoing" | "completed";

interface CompleteTarget {
  id: number;
  giveCount: number;
  receiveCount: number;
  isProposer: boolean;
}

interface Props {
  go: (r: Route) => void;
  openChat: (userId: number) => void;
}

export function TradesScreen({ openChat }: Props) {
  const { user: me, refreshMe } = useAuth();
  const { refresh: refreshInventory } = useInventory();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>("incoming");
  const [proposals, setProposals] = useState<TradeProposal[]>([]);
  const [loading, setLoading] = useState(false);
  const [completeTarget, setCompleteTarget] = useState<CompleteTarget | null>(null);
  const [applyInventory, setApplyInventory] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [counterTarget, setCounterTarget] = useState<TradeProposal | null>(null);
  const [reviewTarget, setReviewTarget] = useState<{ tradeId: number; peerName: string } | null>(null);
  // IDs de trades para los que YA dejé reseña — se calcula al cargar la lista.
  const [reviewedTradeIds, setReviewedTradeIds] = useState<Set<number>>(new Set());

  const reload = async () => {
    setLoading(true);
    try {
      // Recibidas/enviadas muestran TODOS los estados (pending, accepted, rejected,
      // cancelled, countered, completed) — el histórico es importante.
      // Tab "completadas" filtra solo completed para celebrar los exitosos.
      const direction = tab === "completed" ? "all" : tab;
      const status: TradeStatus | undefined = tab === "completed" ? "completed" : undefined;
      const list = await fetchMyProposals(direction, status);
      setProposals(list);
      // Para cada trade completado, chequear si ya dejé reseña — así no
      // mostramos botón "Dejar reseña" cuando ya está hecha.
      const completedIds = list.filter((p) => p.status === "completed").map((p) => p.id);
      if (completedIds.length > 0) {
        const results = await Promise.all(
          completedIds.map((id) => fetchMyReviewForTrade(id).catch(() => null))
        );
        const reviewed = new Set<number>();
        completedIds.forEach((id, i) => {
          if (results[i]) reviewed.add(id);
        });
        setReviewedTradeIds(reviewed);
      } else {
        setReviewedTradeIds(new Set());
      }
    } catch (e) {
      toast(`Error: ${e instanceof ApiError ? e.message : e}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const handleAccept = async (id: number, peerId: number) => {
    try {
      await acceptProposal(id);
      toast("Propuesta aceptada · ahora coordinen por chat");
      // Abrir el chat con el peer automáticamente
      openChat(peerId);
    } catch (e) {
      toast(`Error: ${e instanceof ApiError ? e.message : e}`);
    }
  };

  const handleReject = async (id: number) => {
    if (!confirm("¿Rechazar esta propuesta?")) return;
    try {
      await rejectProposal(id);
      toast("Propuesta rechazada");
      reload();
    } catch (e) {
      toast(`Error: ${e}`);
    }
  };

  const handleCancel = async (id: number) => {
    if (!confirm("¿Cancelar tu propuesta?")) return;
    try {
      await cancelProposal(id);
      toast("Propuesta cancelada");
      reload();
    } catch (e) {
      toast(`Error: ${e}`);
    }
  };

  const openCompleteModal = (p: TradeProposal) => {
    if (!me) return;
    const isProposer = p.from_user.id === me.id;
    // Desde mi perspectiva
    const myGive = isProposer ? p.give_count : p.receive_count;
    const myReceive = isProposer ? p.receive_count : p.give_count;
    setCompleteTarget({ id: p.id, giveCount: myGive, receiveCount: myReceive, isProposer });
    setApplyInventory(true);
  };

  const confirmComplete = async () => {
    if (!completeTarget) return;
    setCompleting(true);
    try {
      await completeProposal(completeTarget.id, applyInventory);
      toast(applyInventory
        ? "¡Trato completado y álbum actualizado!"
        : "¡Trato completado! +1 trade en tu perfil");
      setCompleteTarget(null);
      // Refrescar en paralelo: lista de propuestas, inventario (si se aplicó),
      // y datos del user (trades_completed bumpea). Sin esto el álbum se ve
      // desactualizado hasta que el user recarga la página manualmente.
      reload();
      const refreshTasks: Promise<unknown>[] = [refreshMe()];
      if (applyInventory) refreshTasks.push(refreshInventory());
      await Promise.all(refreshTasks).catch(() => undefined);
    } catch (e) {
      toast(`Error: ${e instanceof ApiError ? e.message : e}`);
    } finally {
      setCompleting(false);
    }
  };

  if (!me) return null;

  return (
    <div className="page">
      <div style={{ display: "flex", alignItems: "flex-end", marginBottom: 16 }}>
        <div>
          <h1 style={{ fontFamily: "var(--font-display)", fontSize: 32, fontWeight: 800, margin: 0, letterSpacing: "-0.02em" }}>Tratos</h1>
          <p style={{ color: "var(--fg-muted)", margin: "4px 0 0" }}>Propuestas directas de intercambio entre coleccionistas.</p>
        </div>
      </div>

      <div className="tabs">
        <button className={"tab " + (tab === "incoming" ? "active" : "")} onClick={() => setTab("incoming")}>
          📩 Recibidas
        </button>
        <button className={"tab " + (tab === "outgoing" ? "active" : "")} onClick={() => setTab("outgoing")}>
          📤 Enviadas
        </button>
        <button className={"tab " + (tab === "completed" ? "active" : "")} onClick={() => setTab("completed")}>
          ✅ Completadas
        </button>
      </div>

      {loading && <div style={{ padding: 32, textAlign: "center", color: "var(--fg-muted)" }}>Cargando…</div>}

      {!loading && proposals.length === 0 && (
        <EmptyState
          icon={tab === "completed" ? "trophy" : "swap"}
          title={
            tab === "incoming" ? "Sin propuestas recibidas" :
            tab === "outgoing" ? "No has enviado propuestas" :
            "Sin intercambios completados aún"
          }
          body={
            tab === "incoming"
              ? "Cuando alguien te proponga un trato, aparecerá aquí."
              : tab === "outgoing"
              ? "Ve a 'Matches' o al perfil de un usuario para proponerle un intercambio."
              : "Tus tratos cerrados se verán aquí con la fecha y reseña."
          }
        />
      )}

      <div className="col" style={{ gap: 12 }}>
        {proposals.map((p) => (
          <ProposalCard
            key={p.id}
            proposal={p}
            myId={me.id}
            onAccept={() => {
              const peerId = p.from_user.id === me.id ? p.to_user.id : p.from_user.id;
              handleAccept(p.id, peerId);
            }}
            onReject={() => handleReject(p.id)}
            onCancel={() => handleCancel(p.id)}
            onComplete={() => openCompleteModal(p)}
            onCounter={() => setCounterTarget(p)}
            onReview={() => {
              const peer = p.from_user.id === me.id ? p.to_user : p.from_user;
              setReviewTarget({ tradeId: p.id, peerName: peer.name });
            }}
            alreadyReviewed={reviewedTradeIds.has(p.id)}
            onChat={() => {
              const peerId = p.from_user.id === me.id ? p.to_user.id : p.from_user.id;
              openChat(peerId);
            }}
          />
        ))}
      </div>

      {completeTarget && (
        <CompleteModal
          target={completeTarget}
          applyInventory={applyInventory}
          setApplyInventory={setApplyInventory}
          onCancel={() => setCompleteTarget(null)}
          onConfirm={confirmComplete}
          submitting={completing}
        />
      )}

      {reviewTarget && (
        <ReviewModal
          tradeProposalId={reviewTarget.tradeId}
          peerName={reviewTarget.peerName}
          onClose={() => setReviewTarget(null)}
          onDone={() => {
            setReviewTarget(null);
            // Refrescar lista — el backend ya recalculó stars_avg del peer y
            // bumpeó level/xp para el reviewer.
            reload();
            refreshMe().catch(() => undefined);
          }}
        />
      )}

      {counterTarget && (
        <ProposeTradeModal
          toUserId={counterTarget.from_user.id}
          toUserName={counterTarget.from_user.name}
          counterOfId={counterTarget.id}
          // Invertimos los items: lo que él me ofrecía (give de la original)
          // ahora yo lo pido (receive). Lo que él me pedía (receive)
          // ahora yo lo ofrezco (give). El usuario puede modificar todo.
          prefilledGive={counterTarget.items.filter((it) => it.direction === "receive").map((it) => it.sticker_id)}
          prefilledReceive={counterTarget.items.filter((it) => it.direction === "give").map((it) => it.sticker_id)}
          // Si el original tenía cash_delta>0 (proposer original encimaba),
          // ahora desde mi perspectiva ÉL me daría dinero → cashSide=them
          prefilledCashDelta={-counterTarget.cash_delta}
          prefilledMessage=""
          onClose={() => setCounterTarget(null)}
          onSent={() => {
            setCounterTarget(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

interface CompleteModalProps {
  target: CompleteTarget;
  applyInventory: boolean;
  setApplyInventory: (v: boolean) => void;
  onCancel: () => void;
  onConfirm: () => void;
  submitting: boolean;
}

function CompleteModal({ target, applyInventory, setApplyInventory, onCancel, onConfirm, submitting }: CompleteModalProps) {
  return (
    <div onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "grid", placeItems: "center", zIndex: 999, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--bg)", borderRadius: 14, maxWidth: 460, width: "100%", padding: 22 }}>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 800, margin: "0 0 10px" }}>
          ¿Confirmar intercambio?
        </h2>
        <p style={{ color: "var(--fg-muted)", margin: "0 0 14px", fontSize: 14 }}>
          Confirma solo cuando ya hayan intercambiado las láminas físicas.
          Marcará el trato como <b>completado</b> y subirá +1 al contador de ambos.
        </p>

        <div style={{
          background: "var(--bg-subtle)",
          borderRadius: 10,
          padding: 12,
          marginBottom: 14,
          fontSize: 13,
        }}>
          <div style={{ display: "flex", justifyContent: "space-around", marginBottom: 8 }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 11, color: "var(--fg-muted)", textTransform: "uppercase", fontWeight: 700 }}>
                Tú entregas
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "var(--primary)" }}>
                {target.giveCount}
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 11, color: "var(--fg-muted)", textTransform: "uppercase", fontWeight: 700 }}>
                Tú recibes
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "var(--accent-amber)" }}>
                {target.receiveCount}
              </div>
            </div>
          </div>
        </div>

        <label style={{
          display: "flex",
          gap: 10,
          alignItems: "flex-start",
          padding: 12,
          border: "1px solid var(--border)",
          borderRadius: 10,
          marginBottom: 14,
          cursor: "pointer",
        }}>
          <input
            type="checkbox"
            checked={applyInventory}
            onChange={(e) => setApplyInventory(e.target.checked)}
            style={{ marginTop: 2 }}
          />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>
              Actualizar mi álbum digital automáticamente
            </div>
            <div style={{ fontSize: 12, color: "var(--fg-muted)", marginTop: 2 }}>
              Restará las que diste y sumará las que recibiste a tu inventario
              (y el del otro). Si prefieres marcarlas tú, desactiva esto.
            </div>
          </div>
        </label>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn btn-secondary" onClick={onCancel} disabled={submitting}>Cancelar</button>
          <button className="btn btn-primary" onClick={onConfirm} disabled={submitting}>
            {submitting ? "Confirmando…" : "✓ Confirmar trato"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface ProposalCardProps {
  proposal: TradeProposal;
  myId: number;
  onAccept: () => void;
  onReject: () => void;
  onCancel: () => void;
  onComplete: () => void;
  onCounter: () => void;
  onReview: () => void;
  alreadyReviewed: boolean;
  onChat: () => void;
}

function ProposalCard({ proposal: p, myId, onAccept, onReject, onCancel, onComplete, onCounter, onReview, alreadyReviewed, onChat }: ProposalCardProps) {
  const isIncoming = p.to_user.id === myId;
  const peer = isIncoming ? p.from_user : p.to_user;
  const giveItems = p.items.filter((it) => it.direction === "give");
  const receiveItems = p.items.filter((it) => it.direction === "receive");

  // Desde la perspectiva del usuario actual:
  // - Si soy proposer (outgoing): GIVE = lo que YO doy, RECEIVE = lo que YO recibo
  // - Si soy receiver (incoming): GIVE = lo que ÉL me da, RECEIVE = lo que YO le doy
  const myGive = isIncoming ? receiveItems : giveItems;
  const myReceive = isIncoming ? giveItems : receiveItems;

  // Cash delta desde mi perspectiva.
  // En la propuesta, cash_delta>0 significa que el proposer original encima dinero.
  // Si yo soy receiver: cash_delta>0 → ÉL me da dinero (lo recibo)
  // Si yo soy proposer: cash_delta>0 → YO doy dinero (lo entrego)
  const cashFromMyView = isIncoming ? -p.cash_delta : p.cash_delta;

  const statusMeta = STATUS_META[p.status] ?? { label: p.status, color: "var(--fg-muted)" };

  return (
    <div className="card trade-card">
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <Avatar user={peer} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>
            {isIncoming ? `${peer.name} te propone` : `Tu propuesta a ${peer.name}`}
          </div>
          <div className="tiny muted">
            {peer.handle ?? `Usuario #${peer.id}`} · {relativeDate(p.created_at)}
          </div>
        </div>
        <div className="chip" style={{ background: statusMeta.color, color: "white" }}>
          {statusMeta.label}
        </div>
      </div>

      {p.message && (
        <div style={{ background: "var(--bg-subtle)", padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
          💬 {p.message}
        </div>
      )}

      {p.counter_of_id && (
        <div className="tiny" style={{ marginBottom: 8, color: "var(--accent-amber)", fontWeight: 700 }}>
          🔄 Esta es una contraoferta a la propuesta original #{p.counter_of_id}
        </div>
      )}

      {cashFromMyView !== 0 && (
        <div style={{
          background: cashFromMyView < 0 ? "rgba(16,185,129,0.12)" : "rgba(236,72,153,0.12)",
          color: cashFromMyView < 0 ? "var(--primary)" : "var(--accent-magenta)",
          padding: "8px 12px",
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
          marginBottom: 12,
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}>
          {cashFromMyView < 0
            ? <>💵 <b>{peer.name}</b> te encima <b>${Math.abs(cashFromMyView).toLocaleString("es-CO")}</b> COP</>
            : <>💸 Tú encimas <b>${Math.abs(cashFromMyView).toLocaleString("es-CO")}</b> COP a {peer.name}</>}
        </div>
      )}

      <div className="trade-card-grid">
        <div className="trade-side">
          <div className="tiny bold muted" style={{ marginBottom: 6 }}>
            TÚ DAS ({myGive.reduce((sum, it) => sum + it.quantity, 0)})
          </div>
          <div className="trade-items">
            {myGive.map((it) => (
              <ItemChip key={it.id} item={it} accent="primary" />
            ))}
          </div>
        </div>
        <div className="trade-arrow">
          <Icon name="swap" size={20} />
        </div>
        <div className="trade-side">
          <div className="tiny bold muted" style={{ marginBottom: 6, textAlign: "right" }}>
            RECIBES ({myReceive.reduce((sum, it) => sum + it.quantity, 0)})
          </div>
          <div className="trade-items" style={{ justifyContent: "flex-end" }}>
            {myReceive.map((it) => (
              <ItemChip key={it.id} item={it} accent="amber" />
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <button className="btn btn-secondary btn-sm" onClick={onChat}>
          <Icon name="chat" size={14} /> Chat
        </button>
        {p.status === "pending" && isIncoming && (
          <>
            <button className="btn btn-secondary btn-sm" onClick={onReject}>Rechazar</button>
            <button className="btn btn-secondary btn-sm" onClick={onCounter} title="Proponer otra combinación">
              🔄 Contraproponer
            </button>
            <button className="btn btn-primary btn-sm" onClick={onAccept}>
              <Icon name="check" size={14} /> Aceptar
            </button>
          </>
        )}
        {p.status === "pending" && !isIncoming && (
          <button className="btn btn-secondary btn-sm" onClick={onCancel}>Cancelar</button>
        )}
        {p.status === "accepted" && (
          <button className="btn btn-primary btn-sm" onClick={onComplete}>
            <Icon name="check" size={14} /> Marcar como completado
          </button>
        )}
        {p.status === "completed" && !alreadyReviewed && (
          <button className="btn btn-primary btn-sm" onClick={onReview}>
            <Icon name="star" size={14} /> Dejar reseña
          </button>
        )}
        {p.status === "completed" && alreadyReviewed && (
          <span className="chip" style={{ fontSize: 11 }}>
            <Icon name="check" size={11} /> Reseña enviada
          </span>
        )}
      </div>
    </div>
  );
}

function ItemChip({ item }: { item: { sticker_num: string; sticker_name: string; quantity: number }; accent: "primary" | "amber" }) {
  return (
    <div className="trade-item-chip" title={item.sticker_name}>
      <span className="trade-item-num">{item.sticker_num}</span>
      <span className="trade-item-name">{item.sticker_name}</span>
      {item.quantity > 1 && <span className="trade-item-qty">×{item.quantity}</span>}
    </div>
  );
}

const STATUS_META: Record<string, { label: string; color: string }> = {
  pending: { label: "Pendiente", color: "var(--accent-amber)" },
  accepted: { label: "Aceptada", color: "var(--primary)" },
  rejected: { label: "Rechazada", color: "var(--fg-muted)" },
  cancelled: { label: "Cancelada", color: "var(--fg-muted)" },
  countered: { label: "Contraofertada", color: "var(--accent-amber)" },
  completed: { label: "Completada", color: "var(--accent-cobalt)" },
  disputed: { label: "En disputa", color: "var(--accent-magenta)" },
};

function relativeDate(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "ahora";
  if (min < 60) return `hace ${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `hace ${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `hace ${day}d`;
  return d.toLocaleDateString("es-CO", { day: "2-digit", month: "short" });
}
